/* ⚠️ GENERATED from app/services/viewTracker.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   👁️ viewTracker.ts — what a screen tells the server about the posts on it.

   🟥 BEFORE: a post that crossed half the screen was a "view" the instant it did
      — a fast scroll past twenty posts was twenty views — and there was no such
      thing as an impression or a reach that differed from it. When the server
      could not be reached, the phone wrote the counters into the post itself,
      which also let any phone write any number there.
   🟢 NOW, TWO EVENTS:
        'i'  the post came on screen          (impressions, and reach once per person)
        'v'  it STAYED there VIEW_MIN_MS        (a view — or a video that played: viewed())
      A post that leaves the screen sooner is an impression and nothing more.
      The same post coming back on screen is another impression only after
      IMP_GAP_MS; a view is sent once per post per session (the server keeps the
      only real tally — this just saves it the traffic).
   🟢 NO FALLBACK WRITE. Events wait in a small queue and are retried; the
      numbers are the server's to keep, and only the server may write them.
   🟢 THE CREATOR'S OWN POSTS ARE NEVER SENT — looking at your own post is not
      an audience.
   Plain logic (no React, no Firebase): the web client runs this file too.
   ═══════════════════════════════════════════════════════════════════════════ */
export const VIEW_MIN_MS = 1500;
export const IMP_GAP_MS = 30 * 60 * 1000;
export const SEND_EVERY_MS = 2000;
export const BATCH_MAX = 50;
export const QUEUE_MAX = 500;
export const SEND_TRIES = 4;
/** Is this post one whose audience we count? (a real post, by somebody else) */
export function countable(p, me) {
  if (!p || !p.id || !p.userId) return false;
  if (p.type === 'reels_section') return false;
  if (me && String(p.userId) === String(me)) return false;
  return true;
}
export function createViewTracker(deps) {
  const now = deps.now || (() => Date.now());
  const setT = deps.setTimer || ((fn, ms) => setTimeout(fn, ms));
  const clearT = deps.clearTimer || (t => clearTimeout(t));
  const minView = typeof deps.minViewMs === 'number' ? deps.minViewMs : VIEW_MIN_MS;
  const every = typeof deps.sendEveryMs === 'number' ? deps.sendEveryMs : SEND_EVERY_MS;

  /** id → when it came on screen this time, and its view timer */
  const onScreen = new Map();
  const lastImp = new Map();
  const viewedIds = new Set();
  let queue = [];
  let sendTimer = null;
  let sending = false;
  function enqueue(ev) {
    if (queue.some(q => q.postId === ev.postId && q.t === ev.t)) return;
    queue.push({
      ...ev,
      tries: 0
    });
    if (queue.length > QUEUE_MAX) queue = queue.slice(queue.length - QUEUE_MAX);
    schedule();
  }
  function schedule() {
    if (sendTimer || !queue.length) return;
    sendTimer = setT(() => {
      sendTimer = null;
      flush().catch(() => {});
    }, every);
  }
  async function flush() {
    if (sending || !queue.length) return 0;
    sending = true;
    const batch = queue.slice(0, BATCH_MAX);
    queue = queue.slice(batch.length);
    let ok = false;
    try {
      ok = await deps.send(batch.map(({
        postId,
        t
      }) => ({
        postId,
        t
      })));
    } catch {
      ok = false;
    }
    if (!ok) {
      /* kept for another go — a deploy or a tunnel is not a reason to lose an audience */
      const again = batch.map(e => ({
        ...e,
        tries: e.tries + 1
      })).filter(e => e.tries < SEND_TRIES);
      queue = again.concat(queue).slice(0, QUEUE_MAX);
    }
    sending = false;
    schedule();
    return ok ? batch.length : 0;
  }
  function markViewed(id) {
    if (viewedIds.has(id)) return;
    viewedIds.add(id);
    enqueue({
      postId: id,
      t: 'v'
    });
  }

  /** The posts on screen right now — the WHOLE set, every time it changes. */
  function visible(posts) {
    const me = deps.me();
    const ids = new Set();
    for (const p of posts || []) {
      if (!countable(p, me)) continue;
      const id = String(p.id);
      ids.add(id);
      if (onScreen.has(id)) continue;
      const t = now();
      const last = lastImp.get(id);
      if (last == null || t - last >= IMP_GAP_MS) {
        lastImp.set(id, t);
        enqueue({
          postId: id,
          t: 'i'
        });
      }
      const timer = viewedIds.has(id) ? null : setT(() => {
        const cur = onScreen.get(id);
        if (cur && cur.timer === timer) markViewed(id);
      }, minView);
      onScreen.set(id, {
        since: t,
        timer
      });
    }
    for (const [id, s] of Array.from(onScreen.entries())) {
      if (ids.has(id)) continue;
      if (s.timer) clearT(s.timer);
      onScreen.delete(id);
    }
  }

  /** A deliberate look: a video that started playing, a post opened full screen. */
  function viewed(p) {
    if (!countable(p, deps.me())) return;
    const id = String(p.id);
    if (!lastImp.has(id)) {
      lastImp.set(id, now());
      enqueue({
        postId: id,
        t: 'i'
      });
    }
    markViewed(id);
  }

  /** The screen went away (blur, background): nothing is on it any more. */
  function clear() {
    visible([]);
  }
  return {
    visible,
    viewed,
    clear,
    flush,
    pending: () => queue.length,
    _onScreen: () => onScreen.size
  };
}
